import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";
import styled from "styled-components";

export type BackgroundType = "fill" | "empty";
export type ButtonVariant = "text" | "outlined" | "contained";

type CommonProps = {
  children?: ReactNode;
  data_testid?: string;
  bgcolor?: string;
  width?: string;
  background?: BackgroundType;
  variant?: ButtonVariant;
  $slim?: boolean;
  disabled?: boolean;
};

export type NativeButtonProps = CommonProps &
  Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "children" | "color" | "disabled"
  > & {
    component?: "button";
    href?: never;
  };

export type AnchorButtonProps = CommonProps &
  Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    "children" | "color" | "href" | "type"
  > & {
    component: "a";
    href: string;
  };

export type ButtonProps = NativeButtonProps | AnchorButtonProps;

type ButtonStyleProps = {
  $bgcolor?: string;
  $width?: string;
  $background: BackgroundType;
  $variant: ButtonVariant;
  $slim: boolean;
  $disabled: boolean;
};

const getBorder = ({
  $disabled,
  $variant,
}: Pick<ButtonStyleProps, "$disabled" | "$variant">) => {
  if ($disabled || $variant === "text") {
    return "none";
  }

  return "2px solid";
};

const getBackgroundColor = ({
  $background,
  $bgcolor,
}: Pick<ButtonStyleProps, "$background" | "$bgcolor">) =>
  $background === "fill" ? ($bgcolor ?? "#FD6520") : "transparent";

const getHoverBackgroundColor = ({
  $background,
  $bgcolor,
}: Pick<ButtonStyleProps, "$background" | "$bgcolor">) =>
  $background === "fill"
    ? ($bgcolor ?? "#FD6520")
    : "rgba(241, 144, 33, 0.1)";

const getTextColor = ({
  $variant,
}: Pick<ButtonStyleProps, "$variant">) =>
  $variant === "contained" ? "white" : "#FD6520";

const getHoverTextColor = ({
  $background,
}: Pick<ButtonStyleProps, "$background">) =>
  $background === "fill" ? "white" : "#FD6520";

export const BaseButton = styled.button<ButtonStyleProps>`
  font-family: Figtree, sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 30px;
  line-height: 32px;
  text-align: center;
  text-transform: none;
  text-decoration: none;
  box-sizing: border-box;
  box-shadow: none;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: ${({ $width }) => $width ?? "100%"};
  padding: ${({ $slim }) => ($slim ? "5px 20px" : "10px 20px")};
  border: ${getBorder};
  border-color: ${({ $bgcolor }) => $bgcolor ?? "#FD6520"};
  border-radius: 30px;

  color: ${getTextColor};
  background-color: ${getBackgroundColor};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  &:hover {
    text-decoration: none;
    box-shadow: none;
    border: ${getBorder};
    border-color: ${({ $bgcolor }) => $bgcolor ?? "#FF5333"};
    color: ${getHoverTextColor};
    background-color: ${getHoverBackgroundColor};
  }
`;

export const BaseLink = styled.a<ButtonStyleProps>`
  font-family: Figtree, sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 30px;
  line-height: 32px;
  text-align: center;
  text-transform: none;
  text-decoration: none;
  box-sizing: border-box;
  box-shadow: none;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: ${({ $width }) => $width ?? "100%"};
  padding: ${({ $slim }) => ($slim ? "5px 20px" : "10px 20px")};
  border: ${getBorder};
  border-color: ${({ $bgcolor }) => $bgcolor ?? "#FD6520"};
  border-radius: 30px;

  color: ${getTextColor};
  background-color: ${getBackgroundColor};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  &:hover {
    text-decoration: none;
    box-shadow: none;
    border: ${getBorder};
    border-color: ${({ $bgcolor }) => $bgcolor ?? "#FF5333"};
    color: ${getHoverTextColor};
    background-color: ${getHoverBackgroundColor};
  }
`;
